import { useCallback, useEffect, useState } from 'react';

import type {
  CompletionStats,
  CostStats,
  DateRange,
  DurationStats,
  EndpointStats,
  ErrorCategory,
  HistogramBucket,
  StatsData,
} from '../../types';
import { computeDuplicationStats } from '../../utils/duplication';
import { listHistoryJobs } from '../api';
import type { Job } from '../types';

/**
 * Stats computation for the 360 ads service. Output shape matches
 * lightning's `StatsData` so the same React chart components can be
 * reused on the Stats page.
 *
 * Differences vs. `useInteriorStats`:
 *
 * * The ads pipeline doesn't have a versioned cropper — the
 *   `versions` slot is left empty and the chart is hidden by the
 *   shared stats components when so.
 * * Per-job cost reuses the FAL ``direct_cost`` sum and, where
 *   present, the Gemini ``direct_cost`` so the metadata-fill call's
 *   spend shows up too.
 * * Error categorisation reuses the same buckets as interior +
 *   lightning + video so error tone is consistent across the SPA.
 */

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

function createHistogram(
  values: number[],
  bucketCount = 10,
  formatLabel: (start: number, end: number) => string = (s, e) =>
    `${s.toFixed(1)}-${e.toFixed(1)}`,
): HistogramBucket[] {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const bucketSize = range / bucketCount || 1;
  const buckets: HistogramBucket[] = [];
  for (let i = 0; i < bucketCount; i++) {
    const rangeStart = min + i * bucketSize;
    const rangeEnd = min + (i + 1) * bucketSize;
    buckets.push({
      range: formatLabel(rangeStart, rangeEnd),
      rangeStart,
      rangeEnd,
      count: 0,
    });
  }
  for (const value of values) {
    const idx = Math.min(
      Math.floor((value - min) / bucketSize),
      bucketCount - 1,
    );
    if (idx >= 0 && idx < buckets.length) buckets[idx].count++;
  }
  return buckets;
}

function formatDurationLabel(start: number, end: number): string {
  const formatSec = (s: number) => {
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return sec > 0 ? `${m}m${sec}s` : `${m}m`;
  };
  return `${formatSec(start)}-${formatSec(end)}`;
}

function formatCostLabel(start: number, end: number): string {
  return `$${start.toFixed(3)}-$${end.toFixed(3)}`;
}

/**
 * Sum of `direct_cost` from every FAL + Gemini endpoint billed for
 * this job. Accepts any object carrying ``fal_usage`` / ``gemini_usage``
 * — both the full {@link Job} row and the trimmed
 * `GET /api/jobs/{id}/images` response satisfy this shape.
 */
export function calculateAdsJobCost(job: {
  fal_usage?: Record<string, unknown> | null;
  gemini_usage?: Record<string, unknown> | null;
}): number {
  let total = 0;
  if (job.fal_usage) {
    for (const usage of Object.values(job.fal_usage)) {
      if (usage && typeof usage === 'object' && 'direct_cost' in usage) {
        const v = (usage as { direct_cost?: number }).direct_cost;
        if (typeof v === 'number') total += v;
      }
    }
  }
  if (job.gemini_usage) {
    for (const usage of Object.values(job.gemini_usage)) {
      if (usage && typeof usage === 'object' && 'direct_cost' in usage) {
        const v = (usage as { direct_cost?: number }).direct_cost;
        if (typeof v === 'number') total += v;
      }
    }
  }
  return total;
}

function categorizeError(errorMessage: string | null | undefined): string {
  if (!errorMessage) return 'Unknown';
  const msg = errorMessage.toLowerCase();
  if (msg.includes('aborted by user')) return 'Aborted';
  if (msg.includes('timeout') || msg.includes('timed out')) return 'Timeout';
  if (msg.includes('rate limit') || msg.includes('429') || msg.includes('quota')) {
    return 'Rate Limited';
  }
  if (msg.includes('invalid') || msg.includes('not found') || msg.includes('404')) {
    return 'Invalid Scan';
  }
  if (msg.includes('engine') || msg.includes('fal') || msg.includes('gemini')) {
    return 'AI Generation Error';
  }
  if (
    msg.includes('api') ||
    msg.includes('fetch') ||
    msg.includes('network') ||
    msg.includes('connection')
  ) {
    return 'API Error';
  }
  return 'Other';
}

function computeDuration(jobs: Job[]): DurationStats {
  const durations = jobs
    .filter((j) => j.status === 'completed' && j.duration_seconds != null)
    .map((j) => j.duration_seconds as number);
  if (durations.length === 0) {
    return { average: 0, median: 0, max: 0, min: 0, histogram: [] };
  }
  return {
    average: durations.reduce((a, b) => a + b, 0) / durations.length,
    median: median(durations),
    max: Math.max(...durations),
    min: Math.min(...durations),
    histogram: createHistogram(durations, 8, formatDurationLabel),
  };
}

function computeCost(jobs: Job[]): CostStats {
  const completed = jobs.filter((j) => j.status === 'completed');
  const costs = completed.map(calculateAdsJobCost);
  const nonZero = costs.filter((c) => c > 0);
  if (nonZero.length === 0) {
    return { average: 0, median: 0, max: 0, min: 0, total: 0, histogram: [] };
  }
  const total = costs.reduce((a, b) => a + b, 0);
  return {
    average: total / completed.length,
    median: median(nonZero),
    max: Math.max(...nonZero),
    min: Math.min(...nonZero),
    total,
    histogram: createHistogram(nonZero, 8, formatCostLabel),
  };
}

function computeCompletion(jobs: Job[]): CompletionStats {
  const completed = jobs.filter((j) => j.status === 'completed').length;
  const failed = jobs.filter((j) => j.status === 'failed').length;
  const total = jobs.length;
  const relevant = completed + failed;
  return {
    completed,
    failed,
    cancelled: 0,
    total,
    completedPercent: relevant > 0 ? (completed / relevant) * 100 : 0,
    failedPercent: relevant > 0 ? (failed / relevant) * 100 : 0,
    cancelledPercent: 0,
  };
}

function computeErrors(jobs: Job[]): ErrorCategory[] {
  const failed = jobs.filter((j) => j.status === 'failed');
  if (failed.length === 0) return [];
  const counts: Record<string, number> = {};
  for (const job of failed) {
    const cat = categorizeError(job.error);
    counts[cat] = (counts[cat] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([category, count]) => ({
      category,
      count,
      percent: (count / failed.length) * 100,
    }))
    .sort((a, b) => b.count - a.count);
}

function computeEndpoints(jobs: Job[]): EndpointStats[] {
  type Acc = {
    totalCalls: number;
    jobsCompleted: number;
    jobsFailed: number;
  };
  const map = new Map<string, Acc>();
  const ensure = (key: string): Acc => {
    let entry = map.get(key);
    if (!entry) {
      entry = { totalCalls: 0, jobsCompleted: 0, jobsFailed: 0 };
      map.set(key, entry);
    }
    return entry;
  };

  for (const job of jobs) {
    if (!job.fal_usage) continue;
    const isCompleted = job.status === 'completed';
    for (const [endpoint, usage] of Object.entries(job.fal_usage)) {
      const calls =
        usage && typeof usage === 'object' && 'calls' in usage
          ? Number((usage as { calls?: number }).calls ?? 0)
          : 0;
      const entry = ensure(endpoint);
      entry.totalCalls += calls;
      if (isCompleted) entry.jobsCompleted++;
      else if (job.status === 'failed') entry.jobsFailed++;
    }
  }

  return Array.from(map.entries())
    .map(([endpoint, data]) => {
      const totalJobs = data.jobsCompleted + data.jobsFailed;
      return {
        endpoint,
        provider: 'fal' as const,
        totalCalls: data.totalCalls,
        jobsCompleted: data.jobsCompleted,
        jobsFailed: data.jobsFailed,
        successRate:
          totalJobs > 0 ? (data.jobsCompleted / totalJobs) * 100 : 0,
        avgDuration: 0,
      };
    })
    .sort((a, b) => b.totalCalls - a.totalCalls);
}

function computeAll(jobs: Job[], dateRange: DateRange): StatsData {
  return {
    duration: computeDuration(jobs),
    cost: computeCost(jobs),
    completion: computeCompletion(jobs),
    duplication: computeDuplicationStats(jobs),
    errors: computeErrors(jobs),
    endpointStats: computeEndpoints(jobs),
    versions: [],
    versionStats: [],
    totalJobs: jobs.length,
    dateRange,
  };
}

export function useAdsStats(dateRange: DateRange) {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!dateRange.from && !dateRange.to) {
      setStats(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const allJobs: Job[] = [];
      const pageSize = 500;
      let page = 1;
      let totalPages = 1;
      do {
        const resp = await listHistoryJobs(dateRange, null, page, pageSize);
        allJobs.push(...resp.jobs);
        totalPages = resp.total_pages;
        if (resp.jobs.length === 0) break;
        page += 1;
      } while (page <= totalPages);
      setStats(computeAll(allJobs, dateRange));
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Failed to fetch statistics');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}

export type DateRangePreset = 'today' | 'last7days' | 'last30days' | 'custom';

export function getDateRangeFromPreset(preset: DateRangePreset): DateRange {
  const now = new Date();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  switch (preset) {
    case 'today': {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'last7days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'last30days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      return { from: start, to: endOfDay };
    }
    case 'custom':
    default:
      return { from: null, to: null };
  }
}
